import React, { useState, useEffect, useRef } from "react";
import { FaShoppingCart, FaChevronDown, FaMapMarkerAlt, FaCrosshairs, FaSearch, FaTimes } from "react-icons/fa";
import logo from "../../Assets/OG Logo.png";
import { useNavigate, useLocation } from "react-router-dom";
import RegisterModal from "../Main/RegisterModal";
import LoginModal from "../Main/LoginModal";
import MyOrdersModal from "../Main/MyOrdersModal";
import CorporateEnquiryModal from "../Main/CorporateEnquiryModal";

const supportedCities = ["Vellore", "Chennai", "Bengaluru", "Hyderabad", "Delhi", "Mumbai", "Kolkata", "Coimbatore", "Kochi", "Salem"];

const Navbar = ({ cartCount }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showOrders, setShowOrders] = useState(false);
  const [showEnquiry, setShowEnquiry] = useState(false);

  // Location Modal & Selection state
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(
    () => localStorage.getItem("userLocation") || "Vellore, TN"
  );
  const [recentLocations, setRecentLocations] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("recentLocations")) || ["Vellore, TN", "Chennai, TN", "Bengaluru, KA"];
    } catch {
      return ["Vellore, TN", "Chennai, TN"];
    }
  });

  const [isFirstVisit, setIsFirstVisit] = useState(() => !localStorage.getItem("hasVisitedLocation"));
  const [locationSearch, setLocationSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isLocating, setIsLocating] = useState(false);
  const [areaNotAvailable, setAreaNotAvailable] = useState(false);
  const [ariaAnnouncement, setAriaAnnouncement] = useState("");

  const navigate = useNavigate();
  const routeLocation = useLocation();
  const locationModalRef = useRef(null);
  const user = JSON.parse(localStorage.getItem("user"));

  // Handle scroll transition (transparent -> solid white with shadow)
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close profile dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".profile-dropdown")) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close Location Modal on ESC key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && showLocationModal) {
        setShowLocationModal(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showLocationModal]);

  // Handle Location Selection
  const handleSelectLocation = (locName) => {
    setCurrentLocation(locName);
    localStorage.setItem("userLocation", locName);
    localStorage.setItem("hasVisitedLocation", "true");
    setIsFirstVisit(false);

    const updatedRecent = [locName, ...recentLocations.filter((l) => l !== locName)].slice(0, 4);
    setRecentLocations(updatedRecent);
    localStorage.setItem("recentLocations", JSON.stringify(updatedRecent));

    setAriaAnnouncement(`Location set to ${locName}`);

    const isSupported = supportedCities.some((c) => locName.toLowerCase().includes(c.toLowerCase()));
    setAreaNotAvailable(!isSupported);

    setTimeout(() => {
      setShowLocationModal(false);
    }, 400);
  };

  // Browser Geolocation Trigger
  const handleUseCurrentLocation = () => {
    setIsLocating(true);
    setAreaNotAvailable(false);

    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          try {
            const res = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
            );
            const data = await res.json();
            const city = data.address?.city || data.address?.town || data.address?.state_district || "Current Area";
            const state = data.address?.state || "";
            const formatted = `${city}${state ? ", " + state : ""}`;
            handleSelectLocation(formatted);
          } catch {
            handleSelectLocation("Vellore, TN");
          } finally {
            setIsLocating(false);
          }
        },
        () => {
          alert("Location permission denied. Please enter your pincode or area manually.");
          setIsLocating(false);
        }
      );
    } else {
      alert("Geolocation is not supported by your browser");
      setIsLocating(false);
    }
  };

  // Handle Live Search Input
  const handleSearchChange = async (e) => {
    const val = e.target.value;
    setLocationSearch(val);
    setAreaNotAvailable(false);

    if (val.trim().length < 3) {
      setSearchResults([]);
      return;
    }

    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&countrycodes=in&q=${encodeURIComponent(val)}`
      );
      const data = await res.json();
      setSearchResults(data.slice(0, 5));
    } catch {
      setSearchResults([]);
    }
  };

  const mobileNavItems = [
    {
      key: "home",
      label: "Home",
      icon: "🏠",
      active: routeLocation.pathname === "/",
      onClick: () => navigate("/"),
    },
    {
      key: "services",
      label: "Services",
      icon: "🛠️",
      active: false,
      onClick: () => {
        const el = document.getElementById("categories-section");
        if (el) el.scrollIntoView({ behavior: "smooth" });
        else navigate("/");
      },
    },
    {
      key: "cart",
      label: "Cart",
      icon: "🛒",
      active: routeLocation.pathname === "/cart",
      badge: cartCount,
      onClick: () => navigate("/cart"),
    },
    {
      key: "orders",
      label: "Orders",
      icon: "📦",
      active: false,
      onClick: () => {
        if (!user) setShowLogin(true);
        else setShowOrders(true);
      },
    },
    {
      key: "enquiry",
      label: "Enquiry",
      icon: "💼",
      active: false,
      onClick: () => setShowEnquiry(true),
    },
  ];

  return (
    <>
      {/* Screen Reader ARIA Live Region */}
      <div className="sr-only" aria-live="polite">
        {ariaAnnouncement}
      </div>

      {/* Main Top Navigation Bar */}
      <nav
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
          isScrolled
            ? "bg-white/95 backdrop-blur-md shadow-[0_2px_16px_rgba(13,0,76,0.08)] py-2.5 text-[#0D004C]"
            : "border-b border-gray-100/80 bg-white/90 py-3.5 text-[#0D004C] backdrop-blur-md"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between gap-3">
          {/* LEFT: Logo & Location Selector */}
          <div className="flex items-center gap-3 sm:gap-5">
            <div
              className="flex items-center cursor-pointer hover:opacity-90 transition shrink-0"
              onClick={() => navigate("/")}
            >
              <img src={logo} alt="Our Genie Logo" className="h-9 sm:h-11 w-auto object-contain" />
            </div>

            <div className="hidden h-6 w-px bg-gray-200 sm:block" />

            {/* Location Selector Pill */}
            <button
              onClick={() => {
                setShowLocationModal(true);
                if (isFirstVisit) {
                  localStorage.setItem("hasVisitedLocation", "true");
                  setIsFirstVisit(false);
                }
              }}
              aria-label={`Change service location, current location is ${currentLocation}`}
              className={`relative flex items-center gap-1.5 px-3 sm:px-4 py-1.5 rounded-full text-xs font-bold transition-all duration-200 ${
                "border border-gray-200 bg-gray-50 text-[#0D004C] hover:border-[#AF7D23] hover:bg-[#fff8e8]"
              }`}
            >
              {isFirstVisit && (
                <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-[#FFB800] animate-ping" />
              )}
              {isFirstVisit && (
                <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-[#FFB800]" />
              )}
              <FaMapMarkerAlt className="text-[#AF7D23]" size={12} />
              <span className="max-w-[100px] sm:max-w-[150px] truncate">{currentLocation}</span>
              <FaChevronDown className="text-[9px] opacity-60 shrink-0" />
            </button>
          </div>

          {/* RIGHT: Actions */}
          <div className="flex items-center gap-2.5 sm:gap-5">
            {/* Corporate Enquiry Button */}
            <button
              onClick={() => setShowEnquiry(true)}
              className="hidden items-center gap-1.5 rounded-full border border-gray-200 bg-white px-4 py-1.5 text-xs font-bold text-[#0D004C] transition-all duration-200 hover:border-[#0D004C] hover:bg-[#0D004C] hover:text-white md:inline-flex"
            >
              Corporate Enquiry
            </button>

            {/* Shopping Cart Icon with Gold Badge */}
            <button
              onClick={() => navigate("/cart")}
              className="relative p-2 rounded-full transition-transform duration-200 hover:scale-110"
              aria-label={`Shopping cart with ${cartCount} items`}
              title="View Cart"
            >
              <FaShoppingCart size={19} className="text-[#0D004C]" />
              {cartCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-[#FFB800] text-[#0D004C] font-black text-[10px] rounded-full min-w-[18px] h-[18px] flex items-center justify-center shadow-md px-1">
                  {cartCount}
                </span>
              )}
            </button>

            {/* Auth / Profile Menu */}
            {!user ? (
              <div className="hidden md:flex items-center gap-2.5">
                <button
                  onClick={() => navigate("/login")}
                  className="rounded-full px-3 py-1.5 text-xs font-bold text-[#0D004C] transition hover:bg-[#0D004C]/[0.06]"
                >
                  Login
                </button>
                <button
                  onClick={() => navigate("/register")}
                  className="bg-[#0D004C] text-white px-4 py-1.5 rounded-full text-xs font-bold hover:bg-[#16007A] transition shadow-[0_4px_14px_rgba(13,0,76,0.25)]"
                >
                  Register
                </button>
              </div>
            ) : (
              <button
                onClick={() => navigate("/orders")}
                className="flex items-center gap-2 focus:outline-none pl-1 pr-3 py-1 rounded-full border border-gray-200 hover:border-[#AF7D23] hover:bg-gray-50 transition"
                title="Go to My Orders & Profile"
              >
                <div className="w-7 h-7 rounded-full bg-[#0D004C] text-[#FFB800] flex items-center justify-center text-xs font-black shadow-sm">
                  {user.name?.charAt(0).toUpperCase() || "U"}
                </div>
                <span className="hidden md:inline font-bold text-xs max-w-[100px] truncate text-[#0D004C]">
                  {user.name}
                </span>
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* LOCATION SELECTION MODAL */}
      {showLocationModal && (
        <div className="fixed inset-0 z-50 bg-[#0D004C]/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 transition-opacity">
          <div
            ref={locationModalRef}
            className="w-full max-w-lg bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden animate-pop flex flex-col max-h-[90vh]"
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <div>
                <h3 className="text-lg font-extrabold text-[#0D004C]">Select Service Location</h3>
                <p className="text-xs text-gray-400 mt-0.5">Find available genies and pricing in your area</p>
              </div>
              <button
                onClick={() => setShowLocationModal(false)}
                aria-label="Close"
                className="w-8 h-8 rounded-full bg-gray-50 hover:bg-gray-100 text-gray-500 hover:text-[#0D004C] flex items-center justify-center transition"
              >
                <FaTimes size={14} />
              </button>
            </div>

            <div className="p-6 overflow-y-auto space-y-5 flex-1">
              {/* Search Area or Pincode Input */}
              <div className="relative">
                <FaSearch className="absolute left-4 top-3.5 text-gray-400" size={13} />
                <input
                  type="text"
                  placeholder="Search your area, pincode or city..."
                  value={locationSearch}
                  onChange={handleSearchChange}
                  className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-2xl text-xs outline-none focus:ring-2 focus:ring-[#0D004C]/30 focus:border-[#0D004C] bg-gray-50/60 font-medium transition"
                />
              </div>

              {/* Search Results Dropdown */}
              {searchResults.length > 0 && (
                <div className="rounded-2xl overflow-hidden divide-y divide-gray-100 bg-gray-50/60 max-h-48 overflow-y-auto">
                  {searchResults.map((item, idx) => (
                    <div
                      key={idx}
                      onClick={() => handleSelectLocation(item.display_name)}
                      className="p-3 text-xs text-gray-700 hover:bg-white cursor-pointer flex items-center gap-2.5 font-medium transition"
                    >
                      <FaMapMarkerAlt className="text-[#0D004C]/60 shrink-0" size={12} />
                      <span className="line-clamp-1">{item.display_name}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Inline Warning: Genie Not in Area Yet */}
              {areaNotAvailable && (
                <div className="bg-[#FFB800]/10 border border-[#FFB800]/30 p-3.5 rounded-2xl text-xs text-[#8a6400] font-semibold flex items-center gap-2.5">
                  <span className="text-base">⚠️</span>
                  <span>Our Genie isn't in this area yet, but we're expanding fast!</span>
                </div>
              )}

              {/* Use Current Location Button */}
              <button
                onClick={handleUseCurrentLocation}
                disabled={isLocating}
                className="w-full flex items-center justify-center gap-2.5 bg-[#0D004C] text-white py-3.5 px-4 rounded-2xl text-xs font-bold hover:bg-[#16007A] transition shadow-[0_6px_16px_rgba(13,0,76,0.2)] disabled:opacity-70"
              >
                <FaCrosshairs className={isLocating ? "animate-spin" : ""} size={15} />
                <span>{isLocating ? "Detecting GPS Location..." : "Use Current Location (GPS)"}</span>
              </button>

              {/* Saved / Recent Addresses */}
              {recentLocations.length > 0 && (
                <div>
                  <h4 className="text-[11px] font-extrabold text-gray-400 uppercase tracking-wider mb-2.5">
                    Saved & Recent Addresses
                  </h4>
                  <div className="space-y-2">
                    {recentLocations.map((loc, idx) => (
                      <div
                        key={idx}
                        onClick={() => handleSelectLocation(loc)}
                        className={`p-3 rounded-2xl text-xs font-semibold flex items-center justify-between cursor-pointer transition ${
                          currentLocation === loc
                            ? "bg-[#0D004C]/[0.06] text-[#0D004C]"
                            : "bg-gray-50/60 text-gray-600 hover:bg-gray-100"
                        }`}
                      >
                        <div className="flex items-center gap-2.5">
                          <FaMapMarkerAlt className={currentLocation === loc ? "text-[#0D004C]" : "text-gray-400"} size={12} />
                          <span>{loc}</span>
                        </div>
                        {currentLocation === loc && (
                          <span className="text-[10px] bg-[#FFB800] text-[#0D004C] px-2 py-0.5 rounded-full font-black">
                            Active
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Other Modals */}
      {showEnquiry && <CorporateEnquiryModal onClose={() => setShowEnquiry(false)} />}
      {showRegister && <RegisterModal onClose={() => setShowRegister(false)} />}
      {showLogin && <LoginModal onClose={() => setShowLogin(false)} />}
      {showOrders && <MyOrdersModal isOpen={showOrders} onClose={() => setShowOrders(false)} />}

      {/* Persistent Mobile Bottom Navigation Bar */}
      <div className="fixed bottom-0 left-0 w-full bg-white/95 backdrop-blur-md border-t border-gray-100 shadow-[0_-4px_20px_rgba(13,0,76,0.08)] md:hidden z-50 py-2 px-2">
        <div className="grid grid-cols-5 items-center text-center">
          {mobileNavItems.map((item) => (
            <button
              key={item.key}
              onClick={item.onClick}
              className="relative flex flex-col items-center gap-0.5 py-1"
            >
              <span
                className={`relative flex items-center justify-center w-8 h-8 rounded-full transition-colors duration-200 ${
                  item.active ? "bg-[#0D004C]/[0.08]" : ""
                }`}
              >
                <span className="text-base leading-none">{item.icon}</span>
                {item.badge > 0 && (
                  <span className="absolute -top-1 -right-1 bg-[#FFB800] text-[#0D004C] text-[9px] font-black rounded-full min-w-[15px] h-[15px] flex items-center justify-center px-0.5">
                    {item.badge}
                  </span>
                )}
              </span>
              <span
                className={`text-[10px] font-bold ${
                  item.active ? "text-[#0D004C]" : "text-gray-400"
                }`}
              >
                {item.label}
              </span>
              {item.active && (
                <span className="absolute -bottom-1.5 w-1 h-1 rounded-full bg-[#FFB800]" />
              )}
            </button>
          ))}
        </div>
      </div>
    </>
  );
};

export default Navbar;